// ─────────────────────────────────────────────────────────────────────────────
// WP-F4-b · 6.4 — «Avisos del sistema»: mantenimientos, suspensiones y avisos puntuales.
//
// 🔴 Este aviso PASA POR ENCIMA de las preferencias del destinatario, y la pantalla lo dice. No es
// contenido del programa: es información operativa sobre la herramienta que la persona está usando,
// y un corte de servicio no puede silenciarlo quien dijo «no quiero saber de campañas».
//
// 🔴 Y la audiencia la resuelve el SERVIDOR. Esta pantalla dice «todos» o «esta OU» y ya está — no
// descarga el padrón para construir una lista de correos, que es lo que hace el camino antiguo de
// Pulso: con eso, quien envía decide a quién llega y una pantalla a medio cargar avisa a menos gente
// sin que nadie se entere.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SYSTEM_BROADCAST_AUDIENCE,
  SYSTEM_BROADCAST_KIND,
  useSendSystemBroadcast,
} from '@/api/recognition';
import type { SystemBroadcastAudience, SystemBroadcastKind } from '@/api/recognition';
import { useOUs } from '@/hooks/useOUs';

/** Los cuatro tipos, en el orden de la maqueta, con su icono y su clave de traducción. */
const KINDS: { value: SystemBroadcastKind; key: string; icon: string }[] = [
  { value: SYSTEM_BROADCAST_KIND.Maintenance, key: 'maintenance', icon: 'fa-screwdriver-wrench' },
  { value: SYSTEM_BROADCAST_KIND.Suspension, key: 'suspension', icon: 'fa-triangle-exclamation' },
  { value: SYSTEM_BROADCAST_KIND.Restored, key: 'restored', icon: 'fa-circle-check' },
  { value: SYSTEM_BROADCAST_KIND.General, key: 'general', icon: 'fa-bullhorn' },
];

export function SystemBroadcastCard() {
  const { t } = useTranslation('recognition');
  const { data: ous = [] } = useOUs();
  const send = useSendSystemBroadcast();

  const [kind, setKind] = useState<SystemBroadcastKind>(SYSTEM_BROADCAST_KIND.Maintenance);
  const [audience, setAudience] = useState<SystemBroadcastAudience>(SYSTEM_BROADCAST_AUDIENCE.Everyone);
  const [ouId, setOuId] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const esOu = audience === SYSTEM_BROADCAST_AUDIENCE.SingleOu;

  // Sin OU elegida, «esta OU» avisaría a toda la organización. El servidor lo rechaza; aquí se
  // impide antes, para que nadie llegue a pulsar el botón con esa combinación.
  const listo = subject.trim().length > 0 && message.trim().length > 0 && (!esOu || ouId !== '');

  const enviar = () => {
    send.mutate(
      {
        kind,
        audience,
        subject: subject.trim(),
        message: message.trim(),
        ouId: esOu ? ouId : null,
      },
      { onSuccess: () => { setSubject(''); setMessage(''); } },
    );
  };

  return (
    <div className="adm-notif-group adm-notif-broadcast">
      <div className="adm-notif-group-head">
        <div className="adm-notif-group-title-row">
          <i className="fas fa-bullhorn adm-notif-broadcast-icon" aria-hidden="true" />
          <h3 className="adm-notif-group-title">{t('admin.notifications.broadcast.title')}</h3>
        </div>
      </div>

      <div className="adm-notif-broadcast-body">
        <p className="adm-notif-item-desc">{t('admin.notifications.broadcast.desc')}</p>

        {/* 🔴 Se dice en la pantalla, no solo en el código: quien manda esto debe saber que llega a
            todo el mundo aunque haya apagado sus notificaciones. */}
        <p className="adm-notif-warn" role="note">
          <i className="fas fa-circle-info" aria-hidden="true" />{' '}
          {t('admin.notifications.broadcast.overridesHint')}
        </p>

        <div className="adm-notif-broadcast-kinds" role="radiogroup" aria-label={t('admin.notifications.broadcast.kindLabel')}>
          {KINDS.map((option) => (
            <button
              key={option.key}
              type="button"
              role="radio"
              aria-checked={kind === option.value}
              className={`adm-notif-kind adm-notif-kind--${option.key}${kind === option.value ? ' is-on' : ''}`}
              onClick={() => setKind(option.value)}
            >
              <i className={`fas ${option.icon}`} aria-hidden="true" />{' '}
              {t(`admin.notifications.broadcast.kind.${option.key}`)}
            </button>
          ))}
        </div>

        <label className="adm-notif-broadcast-field">
          <span className="adm-notif-scope-label">{t('admin.notifications.broadcast.subject')}</span>
          <input
            type="text"
            className="adm-notif-modal-input"
            value={subject}
            placeholder={t('admin.notifications.broadcast.subjectPlaceholder')}
            onChange={(e) => setSubject(e.target.value)}
          />
        </label>

        <label className="adm-notif-broadcast-field">
          <span className="adm-notif-scope-label">{t('admin.notifications.broadcast.message')}</span>
          <textarea
            className="adm-notif-broadcast-textarea"
            rows={3}
            value={message}
            placeholder={t('admin.notifications.broadcast.messagePlaceholder')}
            onChange={(e) => setMessage(e.target.value)}
          />
        </label>

        <div className="adm-notif-broadcast-audience">
          <label className="adm-notif-broadcast-radio">
            <input
              type="radio"
              name="broadcast-audience"
              checked={!esOu}
              onChange={() => setAudience(SYSTEM_BROADCAST_AUDIENCE.Everyone)}
            />
            {t('admin.notifications.broadcast.everyone')}
          </label>
          <label className="adm-notif-broadcast-radio">
            <input
              type="radio"
              name="broadcast-audience"
              checked={esOu}
              onChange={() => setAudience(SYSTEM_BROADCAST_AUDIENCE.SingleOu)}
            />
            {t('admin.notifications.broadcast.singleOu')}
          </label>
          {esOu && (
            <select
              className="adm-notif-freq-select"
              aria-label={t('admin.notifications.broadcast.singleOu')}
              value={ouId}
              onChange={(e) => setOuId(e.target.value)}
            >
              <option value="">{t('admin.notifications.broadcast.chooseOu')}</option>
              {ous.map((ou) => (
                <option key={ou.key} value={ou.key}>{ou.label}</option>
              ))}
            </select>
          )}
        </div>

        {send.isError && (
          <p className="adm-notif-state" role="alert">{t('admin.notifications.broadcast.error')}</p>
        )}

        {/* 🔴 Se enseña el recuento REAL, con los omitidos aparte. «Enviado» a secas escondería que
            a parte de la plantilla no le llegó nada. */}
        {send.isSuccess && send.data && (
          <p className="adm-savebar-ok" role="status">
            <i className="fas fa-check" aria-hidden="true" />{' '}
            {t('admin.notifications.broadcast.sentSummary', {
              sent: send.data.sent,
              recipients: send.data.recipients,
            })}
            {send.data.skipped > 0 && (
              <> {t('admin.notifications.broadcast.skipped', { count: send.data.skipped })}</>
            )}
          </p>
        )}

        <div className="adm-notif-modal-foot">
          <button
            type="button"
            className="btn btn--primary"
            onClick={enviar}
            disabled={!listo || send.isPending}
          >
            <i className="fas fa-paper-plane" aria-hidden="true" />{' '}
            {send.isPending ? t('admin.saving') : t('admin.notifications.broadcast.send')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SystemBroadcastCard;
